/**
 * Practice-level lien filing errors (`lien_creation_errors.stages`).
 * Canonical stage keys: intent, lien, amend, satisfy (API may also store notice/original/amended/satisfaction — readers resolve aliases).
 */

const PRACTICE_DATE_FIELD_TO_STAGE = {
    lien_notice_date: "intent",
    lien_original_date: "lien",
    lien_amended_date: "amend",
    lien_satisfied_date: "satisfy",
    /** Work queue / API shorthand (header "Lien Date" often maps to `lien_date`, not `lien_original_date`) */
    lien_date: "lien",
    notice_date: "intent",
    amended_lien_date: "amend",
    amended_date: "amend",
    satisfaction_date: "satisfy",
    /** Column titles like "Satisfaction" → slug `satisfaction` */
    satisfaction: "satisfy",
    notice: "intent",
};

const KNOWN_LIEN_DATE_FIELDS = Object.keys(PRACTICE_DATE_FIELD_TO_STAGE);

/** CamelCase / kebab → snake_case for matching work-queue `key` / `label` tokens. */
function normalizeFieldToken(raw) {
    if (raw == null || raw === "") {
        return "";
    }
    return String(raw)
        .trim()
        .replace(/-/g, "_")
        .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
        .toLowerCase();
}

function lienStageFromFieldToken(fieldToken) {
    if (fieldToken == null || fieldToken === "") {
        return null;
    }
    const s = normalizeFieldToken(fieldToken);
    if (!s) {
        return null;
    }
    if (PRACTICE_DATE_FIELD_TO_STAGE[s]) {
        return PRACTICE_DATE_FIELD_TO_STAGE[s];
    }
    for (const f of KNOWN_LIEN_DATE_FIELDS) {
        if (s === f || s.endsWith(`_${f}`) || s.endsWith(f)) {
            return PRACTICE_DATE_FIELD_TO_STAGE[f];
        }
    }
    if (s.includes("amend") || s.includes("amended")) {
        return "amend";
    }
    if (s.includes("satisfy") || s.includes("satisfaction")) {
        return "satisfy";
    }
    if (s.includes("notice") || s.includes("intent")) {
        return "intent";
    }
    if (s.includes("lien_date") || (s.includes("lien") && s.includes("date") && !s.includes("amend"))) {
        return "lien";
    }
    return null;
}

/**
 * @param {string} fieldLabel — column `label` from work queue table config (e.g. lien_notice_date)
 * @returns {'intent' | 'lien' | 'amend' | 'satisfy' | null}
 */
export function getLienStageKeyForPracticeDateField(fieldLabel) {
    return lienStageFromFieldToken(fieldLabel);
}

/**
 * Work queue table columns may store the API field on `label` and/or `key` (e.g. key `practices_lien_notice_date`).
 * @param {object | null | undefined} sub_item — grouped_fields item with type "table"
 * @returns {'intent' | 'lien' | 'amend' | 'satisfy' | null}
 */
export function getLienStageKeyForWorkQSubItem(sub_item) {
    if (!sub_item) {
        return null;
    }
    const nameSlug = sub_item.name ? String(sub_item.name).toLowerCase().replace(/\s+/g, "_") : "";
    const candidates = [
        sub_item.label,
        sub_item.key,
        sub_item.field,
        sub_item.slug,
        nameSlug,
    ].filter((x) => x != null && `${x}`.trim() !== "");

    for (const c of candidates) {
        const st = lienStageFromFieldToken(c);
        if (st) {
            return st;
        }
    }
    return null;
}

/**
 * API / serializers may use snake_case or camelCase on practice rows.
 * @param {object | null | undefined} practiceRow — one element of case.practices
 * @returns {object | undefined}
 */
export function pickLienCreationErrors(practiceRow) {
    if (!practiceRow || typeof practiceRow !== "object") {
        return undefined;
    }
    return practiceRow.lien_creation_errors ?? practiceRow.lienCreationErrors;
}

function lienCreationErrorsHasStages(err) {
    return err?.stages && typeof err.stages === "object" && Object.keys(err.stages).length > 0;
}

/**
 * Work-queue / API lien summary rows (inside `liens[]`) vs `case_practice` table rows.
 */
function rowLooksLikeLienRecord(row) {
    if (!row || typeof row !== "object") {
        return false;
    }
    return (
        row.lien_id != null ||
        row.case_lien_id != null ||
        row.lien_status != null ||
        row.liens_status != null ||
        (Object.prototype.hasOwnProperty.call(row, "notice_date") &&
            Object.prototype.hasOwnProperty.call(row, "lien_date"))
    );
}

/**
 * Match one element of `case.liens` (or top-level `liens`) to the table row.
 * When the row is a lien summary, `row.id` is typically the lien id (not case_practice id).
 * When `row.id` is case_practice id, resolve practice then match lien by practice_id + location_id.
 */
function findMatchingLienRecord(liens, row, practices) {
    if (!Array.isArray(liens) || !row) {
        return undefined;
    }
    const byIdStr = (needle) => {
        if (needle == null) {
            return undefined;
        }
        const s = String(needle);
        return liens.find((l) => String(l?.id ?? "") === s);
    };
    const fromExplicit = byIdStr(row.lien_id) ?? byIdStr(row.case_lien_id);
    if (fromExplicit) {
        return fromExplicit;
    }
    if (rowLooksLikeLienRecord(row)) {
        const fromRowId = byIdStr(row.id);
        if (fromRowId) {
            return fromRowId;
        }
    }
    const pid = row.practice_id;
    const locId = row.location_id ?? row.locationId;
    if (pid != null && locId != null) {
        const hit = liens.find((l) => l?.practice_id === pid && (l?.location_id === locId || l?.locationId === locId));
        if (hit) {
            return hit;
        }
    }
    const chart = row.chart_number ?? row.chartNumber;
    if (pid != null && chart != null) {
        const hit = liens.find((l) => l?.practice_id === pid && String(l?.chart_number ?? "") === String(chart));
        if (hit) {
            return hit;
        }
    }
    const cpid = row?.case_practice_id ?? row?.casePracticeId;
    if (cpid != null && Array.isArray(practices)) {
        const idStr = String(cpid);
        const pr = practices.find(
            (p) =>
                p?.id === cpid ||
                p?.case_practice_id === cpid ||
                String(p?.id ?? "") === idStr ||
                String(p?.case_practice_id ?? "") === idStr,
        );
        if (pr) {
            const prPid = pr.practice_id ?? pr.practiceId;
            const prLoc = pr.location_id ?? pr.locationId;
            if (prPid != null && prLoc != null) {
                const hit = liens.find(
                    (l) => l?.practice_id === prPid && (l?.location_id === prLoc || l?.locationId === prLoc),
                );
                if (hit) {
                    return hit;
                }
            }
        }
    }
    return undefined;
}

function pickLienCreationErrorsFromPractices(practices, row) {
    if (!Array.isArray(practices)) {
        return undefined;
    }
    const id = row?.case_practice_id ?? row?.id;
    if (id == null) {
        return undefined;
    }
    const idStr = String(id);
    const alt = practices.find(
        (p) =>
            p?.id === id ||
            p?.case_practice_id === id ||
            String(p?.id ?? "") === idStr ||
            String(p?.case_practice_id ?? "") === idStr,
    );
    return pickLienCreationErrors(alt);
}

/**
 * Prefer errors on the row; else resolve from `case.practices` or `case.liens` on the WorkQ row
 * (`caseData` is the full row: `caseData.case.*`).
 */
export function pickLienCreationErrorsForWorkQ(practiceRow, caseData) {
    const direct = pickLienCreationErrors(practiceRow);
    if (lienCreationErrorsHasStages(direct)) {
        return direct;
    }
    const rowOrCaseAggregate =
        pickLienCreationErrors(caseData) ?? pickLienCreationErrors(caseData?.case);
    if (lienCreationErrorsHasStages(rowOrCaseAggregate)) {
        return rowOrCaseAggregate;
    }
    const practices = caseData?.case?.practices ?? caseData?.practices;
    const liens = caseData?.case?.liens ?? caseData?.liens;

    const fromLienRecord = () => {
        const hit = findMatchingLienRecord(liens, practiceRow, practices);
        return pickLienCreationErrors(hit);
    };
    const fromPracticeRecord = () => pickLienCreationErrorsFromPractices(practices, practiceRow);

    if (rowLooksLikeLienRecord(practiceRow)) {
        const fromLien = fromLienRecord();
        if (lienCreationErrorsHasStages(fromLien)) {
            return fromLien;
        }
        const fromPractice = fromPracticeRecord();
        if (lienCreationErrorsHasStages(fromPractice)) {
            return fromPractice;
        }
        return direct ?? rowOrCaseAggregate ?? fromLien ?? fromPractice ?? undefined;
    }

    const fromPractice = fromPracticeRecord();
    if (lienCreationErrorsHasStages(fromPractice)) {
        return fromPractice;
    }
    const fromLien = fromLienRecord();
    if (lienCreationErrorsHasStages(fromLien)) {
        return fromLien;
    }
    return direct ?? rowOrCaseAggregate ?? fromPractice ?? fromLien ?? undefined;
}

/**
 * Map a `case_liens.type` value (or API alias) to the canonical `lien_creation_errors.stages` key.
 * @param {string | null | undefined} lienRowType
 * @returns {'intent' | 'lien' | 'amend' | 'satisfy' | null}
 */
export function canonicalLienStageKeyFromLienRowType(lienRowType) {
    const t = `${lienRowType ?? ""}`.trim().toLowerCase();
    if (t === "" || t === "null" || t === "undefined") {
        return null;
    }
    if (t === "intent" || t === "notice") {
        return "intent";
    }
    if (t === "lien" || t === "original") {
        return "lien";
    }
    if (t === "amend" || t === "amended") {
        return "amend";
    }
    if (t === "satisfy" || t === "satisfaction") {
        return "satisfy";
    }
    return null;
}

/** API may use alternate stage keys (e.g. `notice` vs `intent`). */
const STAGE_READ_ALIASES = {
    intent: ["intent", "notice"],
    lien: ["lien", "original"],
    amend: ["amend", "amended"],
    satisfy: ["satisfy", "satisfaction"],
};

/**
 * @param {object | null | undefined} stages — `lien_creation_errors.stages`
 * @param {'intent' | 'lien' | 'amend' | 'satisfy'} canonicalStage
 * @returns {object | undefined}
 */
function findStageBlockForCanonicalStage(stages, canonicalStage) {
    if (!stages || typeof stages !== "object" || canonicalStage == null) {
        return undefined;
    }
    const keysToTry = STAGE_READ_ALIASES[canonicalStage] ?? [canonicalStage];
    for (const k of keysToTry) {
        if (stages[k] != null) {
            return stages[k];
        }
    }
    return undefined;
}

/**
 * Resolve `stages.*` payload for one lien row (matches backend canonical keys: lien, not original).
 * @param {object | null | undefined} lienCreationErrors
 * @param {string | null | undefined} lienRowType — e.g. `item.type` from CaseLien
 * @returns {object | undefined}
 */
export function getLienStageDataFromLienRowType(lienCreationErrors, lienRowType) {
    const stages = lienCreationErrors?.stages;
    if (!stages || typeof stages !== "object") {
        return undefined;
    }
    const canonical = canonicalLienStageKeyFromLienRowType(lienRowType);
    if (canonical != null) {
        const fromCanonical = findStageBlockForCanonicalStage(stages, canonical);
        if (fromCanonical != null) {
            return fromCanonical;
        }
    }
    if (lienRowType != null && stages[lienRowType] != null) {
        return stages[lienRowType];
    }
    return undefined;
}

function stagePayload(stageData) {
    if (!stageData || typeof stageData !== "object") {
        return { georgiaClerkErrors: null, errorsAll: null };
    }
    const byCat = stageData.errors_by_category ?? stageData.errorsByCategory;
    const georgiaClerkErrors = byCat?.georgia_clerk_errors ?? byCat?.georgiaClerkErrors;
    const forceErrors = byCat?.force_errors ?? byCat?.forceErrors;
    const fromAll = stageData.all_errors ?? stageData.allErrors;
    let errorsAll = null;
    if (Array.isArray(georgiaClerkErrors) && georgiaClerkErrors.length > 0) {
        errorsAll = georgiaClerkErrors;
    } else if (Array.isArray(forceErrors) && forceErrors.length > 0) {
        errorsAll = forceErrors;
    } else {
        errorsAll = fromAll;
    }
    if (
        (!errorsAll || (Array.isArray(errorsAll) && errorsAll.length === 0)) &&
        byCat &&
        typeof byCat === "object"
    ) {
        const merged = Object.values(byCat)
            .filter(Array.isArray)
            .flat()
            .map((item) => (item == null ? "" : `${item}`.trim()))
            .filter(Boolean);
        if (merged.length > 0) {
            errorsAll = merged;
        }
    }
    return { georgiaClerkErrors, errorsAll };
}

/**
 * @param {object | null | undefined} lienCreationErrors — practice.lien_creation_errors
 * @param {'intent' | 'lien' | 'amend' | 'satisfy'} stageType
 * @returns {string[]}
 */
export function getLienStageErrorMessages(lienCreationErrors, stageType) {
    if (stageType == null) {
        return [];
    }
    const stageData = findStageBlockForCanonicalStage(lienCreationErrors?.stages, stageType);
    const { errorsAll } = stagePayload(stageData);

    let errorMessages = [];
    if (Array.isArray(errorsAll)) {
        errorMessages = errorsAll;
    } else if (errorsAll && typeof errorsAll === "object") {
        errorMessages = Object.values(errorsAll).flat();
    } else if (errorsAll) {
        errorMessages = [errorsAll];
    }
    return errorMessages.map((item) => (item == null ? "" : `${item}`.trim())).filter(Boolean);
}

/**
 * Work-queue date columns: same mapping as {@link getLienStageErrorMessages} — each column shows
 * errors for that stage only (intent/notice, lien/original, amend, satisfy). No cross-stage fallback.
 */
export function getWorkQDateColumnLienMessages(lienCreationErrors, stageType) {
    return getLienStageErrorMessages(lienCreationErrors, stageType);
}
